/**
 * DialogShell + useDialogA11y — the account area's modal chrome.
 *
 * WHY THIS EXISTS
 *
 * `DeleteAccountDialog` shipped with a hand-rolled focus trap, Escape handler
 * and backdrop, written inline (STORY-001-010 kept the story inside its
 * allowed-touch list by not reaching for shadcn's Dialog). That trap is correct
 * and well-tested, so it is extracted here verbatim rather than rewritten —
 * and now a SECOND destructive flow (confirming a log-out) can reuse it instead
 * of growing a third, subtly-different copy.
 *
 * The chrome itself is the counter slip in modal form: a ruled sheet, a drawn
 * header rule, a sunken footer band holding the actions. No blur, no floating
 * card-in-a-card. On mobile it sits on the bottom edge (thumb reach); from `sm`
 * up it centres.
 *
 * Accessibility contract (unchanged from the original, which the axe spec
 * gates): `role="dialog"`, `aria-modal`, focus moves in on open and returns to
 * the invoker on close, Tab cycles inside, Escape closes, the backdrop closes
 * on a direct click.
 */
import clsx from "clsx";
import { X } from "lucide-react";
import { useEffect, useRef, type ReactElement, type ReactNode, type RefObject } from "react";

import { iconProps } from "@/lib/icons";

/**
 * Wires focus containment, Escape-to-close and focus restoration for an open
 * dialog. Returns the ref to spread on the dialog panel.
 */
export function useDialogA11y(
  open: boolean,
  onClose: () => void,
): RefObject<HTMLDivElement | null> {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const root = dialogRef.current;
    if (root !== null) {
      const first = root.querySelector<HTMLElement>(
        "input, button, [tabindex]:not([tabindex='-1'])",
      );
      first?.focus();
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Tab" && root !== null) {
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            "input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])",
          ),
        );
        if (focusables.length === 0) return;
        const firstEl = focusables[0]!;
        const lastEl = focusables[focusables.length - 1]!;
        if (event.shiftKey && document.activeElement === firstEl) {
          event.preventDefault();
          lastEl.focus();
        } else if (!event.shiftKey && document.activeElement === lastEl) {
          event.preventDefault();
          firstEl.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKey);
    return (): void => {
      document.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  return dialogRef;
}

export interface DialogShellProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading. Danger-typed when `tone` is `danger`. */
  title: string;
  tone?: "default" | "danger";
  /** Accessible label for the close control. */
  closeLabel: string;
  /** The action row, rendered in the sunken footer band. */
  footer: ReactNode;
  children: ReactNode;
  testId?: string;
  backdropTestId?: string;
  closeTestId?: string;
  titleId?: string;
}

/** The ruled modal sheet. Renders nothing when closed. */
export function DialogShell({
  open,
  onClose,
  title,
  tone = "default",
  closeLabel,
  footer,
  children,
  testId,
  backdropTestId,
  closeTestId,
  titleId = "garden-dialog-title",
}: DialogShellProps): ReactElement | null {
  const dialogRef = useDialogA11y(open, onClose);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--g-scrim)] px-4 py-6 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid={backdropTestId}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-[440px] overflow-hidden rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] shadow-[var(--g-shadow-xl)]"
        data-testid={testId}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--g-rule-strong)] px-5 py-4">
          <h2
            id={titleId}
            className={clsx(
              "font-display text-[18px] font-extrabold leading-tight",
              tone === "danger" ? "text-[var(--g-error)]" : "text-foreground",
            )}
          >
            {title}
          </h2>
          <button
            type="button"
            className="-mr-1.5 -mt-1 grid size-11 shrink-0 place-items-center rounded-[var(--g-radius)] text-[var(--g-ink-2)] transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onClose}
            aria-label={closeLabel}
            data-testid={closeTestId}
          >
            <X {...iconProps("md")} />
          </button>
        </div>

        <div className="px-5 py-4">{children}</div>

        <div className="flex flex-col-reverse gap-2 border-t border-[var(--g-rule)] bg-[var(--g-sunken)] px-5 py-4 sm:flex-row sm:justify-end">
          {footer}
        </div>
      </div>
    </div>
  );
}
